// Список норм группами ответа: действует / зависит / противоречие / снято /
// не касается.
//
// Заголовок группы прилипает к верху листа: в длинном списке иначе не видно,
// в какой группе читаешь. Противоречие стоит скобкой: две нормы пары рядом,
// суть — под ними. Общего итога «можно / нельзя» здесь нет намеренно: запрет и
// разрешение в одной точке — данные приказа, и решать их спор за него раздел
// не вправе.

import { useEffect, useRef, useState } from 'react'
import { useT } from '../i18n'
import type { RuleNorm } from '../api'
import { RuleRow } from './RuleRow'
import { COLLAPSED_BY_DEFAULT, groupNorms, pairChunks } from './rulesGroups'
import type { RuleGroup } from './rulesGroups'

/** Цвет группы. Цвет — смысл, а не украшение: действующий запрет — тон
 *  ошибки (он же у заливки запретов на карте), «зависит» — янтарь внимания,
 *  снятое — тон «положительно», противоречие — акцент «прочти». Классы
 *  выписаны целиком: Tailwind ищет их в исходнике как текст. */
export const GROUP_FILL: Record<RuleGroup, string> = {
  applies: 'bg-coral',
  depends: 'bg-buoy',
  conflict: 'bg-sonar',
  excepted: 'bg-ok',
  idle: 'bg-hull',
}

/** Переход к группе из сводки: `at` различает повторный клик по той же. */
export interface GroupJump { key: RuleGroup; at: number }

export function RuleGroups({ norms, selected, onSelect, quoteLang, instruments, jump }: {
  norms: RuleNorm[]
  /** Язык цитат каталога (`quote_lang` ответа). */
  quoteLang?: string
  instruments?: Record<string, import('../api').RulesInstrument>
  selected: string | null
  onSelect: (code: string | null) => void
  jump?: GroupJump | null
}) {
  const t = useT()
  const [collapsed, setCollapsed] = useState<Set<RuleGroup>>(() => new Set(COLLAPSED_BY_DEFAULT))
  const sections = useRef(new Map<RuleGroup, HTMLElement>())
  const toggle = (g: RuleGroup) => setCollapsed(s => {
    const next = new Set(s)
    if (next.has(g)) next.delete(g)
    else next.add(g)
    return next
  })

  // Переход из сводки раскрывает группу и подводит к ней список.
  useEffect(() => {
    if (!jump) return
    setCollapsed(s => {
      if (!s.has(jump.key)) return s
      const next = new Set(s)
      next.delete(jump.key)
      return next
    })
    // Прокручивается только список листа: `scrollIntoView` двигал бы и
    // страницу вокруг карты.
    const el = sections.current.get(jump.key)
    const list = el?.closest<HTMLElement>('[data-rules-scroll]')
    if (!el || !list) return
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const top = el.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop
    list.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' })
  }, [jump])

  const row = (n: RuleNorm) => (
    <RuleRow key={n.code} norm={n} open={selected === n.code} quoteLang={quoteLang}
      origLang={instruments?.[n.instrument]?.lang}
      onToggle={() => onSelect(selected === n.code ? null : n.code)} />
  )
  return (
    <div>
      {groupNorms(norms).map(g => {
        const open = !collapsed.has(g.key)
        return (
          <section key={g.key} aria-label={t(`rules.group.${g.key}`)}
            ref={el => { if (el) sections.current.set(g.key, el); else sections.current.delete(g.key) }}>
            <h3 className="sticky top-0 z-[1] border-b border-hull bg-abyss">
              <button type="button" aria-expanded={open} onClick={() => toggle(g.key)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-haze focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-sonar">
                <span aria-hidden className={`h-3 w-1 shrink-0 rounded-full ${GROUP_FILL[g.key]}`} />
                <span className="font-heading text-[13px] font-semibold text-foam">
                  {t(`rules.group.${g.key}`)}
                </span>
                <span className="tnum text-[12px] text-mist">{g.norms.length}</span>
                <svg aria-hidden viewBox="0 0 12 12"
                  className={`ml-auto h-3 w-3 shrink-0 text-faint transition-transform motion-reduce:transition-none ${open ? '' : '-rotate-90'}`}>
                  <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </h3>
            {open && g.key === 'conflict' && (
              <div className="space-y-2 px-3 py-2">
                {pairChunks(g.norms).map(chunk => (
                  <div key={chunk.map(n => n.code).join('|')}
                    className="overflow-hidden rounded-lg border border-dashed border-sonar/60">
                    <ul>{chunk.map(row)}</ul>
                    {[...new Set(chunk.flatMap(n => n.conflicts.map(c => c.detail)))].map(d => (
                      <p key={d} className="bg-sonar/10 px-3 py-1.5 text-[11px] text-mist">{d}</p>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {open && g.key !== 'conflict' && <ul>{g.norms.map(row)}</ul>}
          </section>
        )
      })}
    </div>
  )
}
